import React, { useState, useEffect } from "react";
import Pagination from "react-responsive-pagination";
import cookie from "react-cookies";
import { toast } from "react-toastify";
import axios from "utils/axios";
import { Form, Field } from "react-final-form";
import FinalFormInput from "components/FinalFormInput";
import { scrollToTop } from "utils/utils";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import AddressAutocomplete from "components/AddressAutocomplete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import DeleteIcon from "@mui/icons-material/Delete";

const options = ["Food", "Health", "Sleep", "Legal", "Education", "Library"];

const Resources = () => {
  const [formData, setFormData] = useState<Array<any>>();
  const [open, setOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [showCount, setShowCount] = useState<number>(10);
  const [editable, setEditable] = useState<boolean>(false);
  const [resourceField, setResourceField] = useState<any>({});
  const [address, setAddress] = useState<string>("");
  const [street, setStreet] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const navigate = useNavigate();

  const loadPage = async () => {
    try {
      const response = await axios.get(
        `/api/resource/read/${showCount}/${currentPage}`
      );
      setTotalPages(Math.ceil(response.data.count / showCount));
      setFormData(response.data.rows);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  useEffect(() => {
    const addressForm = () => {
      if (editable && open) {
        const ss = document.getElementById("addressUpdate") as HTMLInputElement;
        const autocomplete = new window.google.maps.places.Autocomplete(ss, {
          types: ["address"],
        });
        autocomplete.setComponentRestrictions({
          country: ["us"],
        });
        autocomplete.addListener("place_changed", function (this: any) {
          const place = this.getPlace();
          let tempAddress = place.formatted_address;
          const value = tempAddress.split(", ");
          const count = value.length;
          let tempCity = value[count - 3];
          let tempState = value[count - 2].slice(0, 2);
          let tempStreet = place.name;
          setStreet(tempStreet);
          setCity(tempCity);
          setState(tempState);
          setAddress(tempAddress);
        });
      }
    };
    setTimeout(() => {
      addressForm();
    }, 300);
  }, [editable, open]); //eslint-disable-line

  useEffect(() => {
    scrollToTop();
    loadPage();
  }, [currentPage, showCount]); //eslint-disable-line

  const handlePageChange = (e: number) => {
    setCurrentPage(e);
  };
  const handleCountChange = (e: number) => {
    if (totalPages === 1 && e > showCount) {
      toast.warning(
        "All results displayed! You can see all the resources currently."
      );
      return;
    }
    setCurrentPage(1);
    setShowCount(e);
  };

  const handleRead = (resource: any) => {
    setResourceField(resource);
    setEditable(false);
    setOpen(true);
  };

  const handleEdit = (resource: any) => {
    setResourceField(resource);
    setAddress(resource.address);
    setState(resource.state);
    setCity(resource.city);
    setStreet(resource.street);
    setEditable(true);
    setOpen(true);
  };

  const handleUpdate = async (val: any) => {
    let {
      id,
      type,
      name,
      phoneNumber,
      email = "",
      websiteLink = "",
      description,
    } = val;
    phoneNumber = phoneNumber.replace(/\D/g, "");
    if (phoneNumber.length !== 10) {
      toast.warning("Please type 10 digit phone number");
      return;
    }
    if (
      email !== "" &&
      !email
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      toast.warning("Please type vaild email address");
      return;
    }
    if (websiteLink !== "" && !websiteLink.includes("https://"))
      websiteLink = "https://" + websiteLink;
    Swal.fire({
      title: "<h6>Are you sure you want to update this resource?</h6>",
      showDenyButton: true,
      customClass: "z-index: 3",
      showCancelButton: false,
      confirmButtonText: `Confirm`,
      denyButtonText: `Cancel`,
    }).then(async (result) => {
      /* Read more about isConfirmed, isDenied below */
      if (result.isConfirmed) {
        try {
          const response = await axios.put("/api/resource/update", {
            id,
            type,
            name,
            address,
            city,
            state,
            street,
            phoneNumber,
            email,
            websiteLink,
            description,
          });
          toast.success(response.data.message);
          loadPage();
          handleClose();
        } catch (error: any) {
          toast.error(error.response.data.message);
        }
      } else if (result.isDenied) {
        return;
      }
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: "<h6>Are you sure you want to delete this resource?</h6>",
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText: `Confirm`,
      denyButtonText: `Cancel`,
    }).then(async (result) => {
      /* Read more about isConfirmed, isDenied below */
      if (result.isConfirmed) {
        try {
          const result = await axios.delete(`/api/resource/delete/${id}`);
          loadPage();
          toast.success(result.data.message);
          navigate("/dashboard");
        } catch (error: any) {
          toast.error(error.response.data.message);
        }
      } else if (result.isDenied) {
        return;
      }
    });
  };

  const handleCreate = () => {
    window.open("/create-resource", "_blank");
  };

  const handleClick = (errors: any, values: any) => {};

  return (
    <>
      <div className="main-text text-right sm:text-center">
        <span
          className={`cursor-pointer ${
            showCount === 10 ? "text-button font-extrabold" : ""
          }`}
          onClick={() => handleCountChange(10)}
        >
          10
        </span>
        {" | "}
        <span
          className={`cursor-pointer ${
            showCount === 25 ? "text-button font-extrabold" : ""
          }`}
          onClick={() => handleCountChange(25)}
        >
          25
        </span>
        {" | "}
        <span
          className={`cursor-pointer ${
            showCount === 100 ? "text-button font-extrabold" : ""
          }`}
          onClick={() => handleCountChange(100)}
        >
          100
        </span>
      </div>
      <div className="grid gap-[8px]">
        {formData?.map((resource, index) => (
          <div
            className="px-[12px] py-[8px] flex sm:flex-col items-center justify-between sm:items-start bg-gray main-text border-solid border-[1.5px] border-[#bdbdbd] rounded-sm shadow-md hover:shadow-none cursor-default"
            key={index}
          >
            <span className="flex gap-[16px] items-center">
              {resource.name}
            </span>
            <div className="flex sm:ml-auto sm:mt-[20px]">
              <div
                onClick={() => handleRead(resource)}
                className="cursor-pointer"
              >
                <VisibilityIcon color="primary" />
              </div>
              {cookie.load("user") &&
                (cookie.load("user").userType === "admin" ||
                  cookie.load("user").userType === "super_user") && (
                  <>
                    {" | "}
                    <div
                      onClick={() => handleEdit(resource)}
                      className="cursor-pointer"
                    >
                      <ModeEditOutlineIcon color="success" />
                    </div>
                    {" | "}
                    <div
                      onClick={() => handleDelete(resource.id)}
                      className="cursor-pointer"
                    >
                      <DeleteIcon color="error" />
                    </div>
                  </>
                )}
            </div>
          </div>
        ))}
        <Pagination
          total={totalPages}
          current={currentPage}
          onPageChange={(page) => handlePageChange(page)}
          maxWidth={300}
        />
        <Dialog className="!z-1" open={open} onClose={handleClose}>
          <Form
            onSubmit={(val) => handleUpdate(val)}
            initialValues={resourceField}
            render={({ handleSubmit, errors, values }) => (
              <form onSubmit={handleSubmit} autoComplete="off">
                <DialogTitle>Resource Detail</DialogTitle>
                <DialogContent>
                  <div className="grid gap-[12px] mt-[12px] w-[480px] sm:w-full">
                    <Field id="type" name="type" placeholder="Type">
                      {(props: any) => (
                        <FinalFormInput
                          {...props}
                          component="select"
                          options={options}
                          readOnly={!editable}
                          required
                        />
                      )}
                    </Field>
                    <Field
                      id="name"
                      name="name"
                      placeholder="Name"
                      render={(props: any) => (
                        <FinalFormInput
                          {...props}
                          readOnly={!editable}
                          required
                        />
                      )}
                    ></Field>
                    <Field
                      id="addressUpdate"
                      name="address"
                      placeholder="Address"
                    >
                      {(props: any) => (
                        <AddressAutocomplete
                          {...props}
                          defaultValue={`${resourceField.address}`}
                          required
                          readOnly={!editable}
                        />
                      )}
                    </Field>
                    <Field
                      id="phoneNumber"
                      name="phoneNumber"
                      placeholder="Phone Number"
                    >
                      {(props: any) => (
                        <FinalFormInput
                          {...props}
                          readOnly={!editable}
                          required
                        />
                      )}
                    </Field>
                    <Field id="email" name="email" placeholder="Email">
                      {(props: any) => (
                        <FinalFormInput {...props} readOnly={!editable} />
                      )}
                    </Field>
                    <Field
                      id="websiteLink"
                      name="websiteLink"
                      placeholder="Website Link"
                    >
                      {(props: any) => (
                        <FinalFormInput {...props} readOnly={!editable} />
                      )}
                    </Field>
                    <Field
                      id="description"
                      name="description"
                      placeholder="Description"
                      rows={8}
                    >
                      {(props: any) => (
                        <FinalFormInput
                          {...props}
                          component="textarea"
                          readOnly={!editable}
                          required
                        />
                      )}
                    </Field>
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose}>
                    {editable ? "Cancel" : "Close"}
                  </Button>
                  {editable && (
                    <Button
                      type="submit"
                      onClick={() => handleClick(errors, values)}
                    >
                      Update
                    </Button>
                  )}
                </DialogActions>
              </form>
            )}
          />
        </Dialog>
      </div>
      <button className="high-emphasis mt-[16px]" onClick={handleCreate}>
        CREATE A RESOURCE
      </button>
    </>
  );
};

export default Resources;
